<template>
  <router-link
    v-if="canUseMatchmaking && activeLobby && !viewingActiveLobby"
    :to="{ name: 'MatchmakingLobbyPage', params: { id: activeLobby.uuid } }"
    class="inline-flex items-center flex-shrink-0 rounded-lg bg-green-950 ring-1 ring-green-800 font-semibold text-green-300 hover:bg-green-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
    :class="compact ? 'h-8 gap-2 px-2.5 text-xs' : 'h-10 gap-3 px-3 text-sm'"
  >
    <span class="relative flex w-2 h-2" aria-hidden="true">
      <span class="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 motion-safe:animate-ping" />
      <span class="relative inline-flex w-2 h-2 rounded-full bg-green-500" />
    </span>
    {{ $t('play.matchmaking.go_to_matchroom') }}
    <ArrowRightIcon :class="compact ? 'w-3.5 h-3.5' : 'w-4 h-4'" aria-hidden="true" />
  </router-link>
  <div
    v-else-if="canUseMatchmaking && isQueued && !activeLobby"
    class="inline-flex items-center flex-shrink-0 rounded-lg ring-1"
    :class="[compact ? 'h-8 gap-2 text-xs' : 'h-10 gap-3 text-sm', isOwner ? 'pr-1' : 'pr-3',
             lan ? 'bg-secondary-950 ring-secondary-800' : 'bg-primary-950 ring-primary-800']"
    role="status"
  >
    <router-link
      :to="searchTarget"
      class="inline-flex items-center h-full rounded-l-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      :class="compact ? 'gap-2 pl-2.5' : 'gap-3 pl-3'"
    >
      <span class="relative flex w-2 h-2" aria-hidden="true">
        <span class="absolute inline-flex w-full h-full rounded-full opacity-75 motion-safe:animate-ping" :class="lan ? 'bg-secondary-400' : 'bg-primary-400'" />
        <span class="relative inline-flex w-2 h-2 rounded-full" :class="lan ? 'bg-secondary-400' : 'bg-primary-500'" />
      </span>
      <span class="font-semibold" :class="lan ? 'text-secondary-200' : 'text-primary-200'">{{ searchLabel }}</span>
      <span class="tabular-nums text-text-secondary">{{ elapsedLabel }}</span>
    </router-link>
    <button
      v-if="isOwner"
      type="button"
      @click="cancelMatchmaking"
      class="inline-flex items-center gap-1.5 rounded-md font-semibold text-text-secondary hover:text-text-primary hover:bg-primary-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      :class="compact ? 'h-6 px-2' : 'h-8 px-2.5'"
    >
      <XMarkIcon :class="compact ? 'w-3.5 h-3.5' : 'w-4 h-4'" aria-hidden="true" />
      {{ $t('common.cancel') }}
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useQueueSearch } from '../../composables/useMatchmakingQueue';

defineProps({
  compact: { type: Boolean, default: false },
});

const auth = useAuth();
const canUseMatchmaking = computed(() => !!auth.user()?.can_use_matchmaking);

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const {
  queueEntry, isQueued, isOwner, activeLobby, elapsedLabel, cancelMatchmaking,
} = useQueueSearch();

// The LAN party this queue entry belongs to, if any: purple, and named after the Creator.
const lan = computed(() => {
  const membership = store.getters.matchmakingContext?.lan_party;
  const uuid = queueEntry.value?.lan_party_uuid;
  return uuid && membership?.uuid === uuid ? membership : null;
});
const searchTarget = computed(() => (lan.value ? `/lan/${lan.value.creator}` : '/matchmaking'));
const searchLabel = computed(() => {
  if (!lan.value) return t('play.matchmaking.searching');
  return lan.value.wait_for_creator
    ? t('play.lan_party.waiting_for_seat', { name: lan.value.creator_name })
    : t('play.lan_party.title');
});

const viewingActiveLobby = computed(
  () => route.name === 'MatchmakingLobbyPage' && route.params.id === activeLobby.value?.uuid,
);
</script>
